import React from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import PathogenCard from '@components/shared/design/PathogenCard';
import { PageSlice, SlicePathogenListDetails } from '@contracts';
import { getPathogensData } from '@data/pathogensData';
import { useCountry } from '@hooks/useCountry';
import LazyLoadImage from '../LazyLoadImage';
import { TEXT_STYLES, TextVariant } from './Typography';

interface PathogenListProps {
  slice: PageSlice;
}

const PathogenList: React.FC<PathogenListProps> = ({ slice }) => {
  const { t } = useTranslation();
  const sliceDetails = slice.primary as SlicePathogenListDetails;
  const { country } = useCountry();
  const pathogensData = getPathogensData({ screeningType: sliceDetails?.screening_type, country });

  return (
    <PathogenListContainer>
      <Wrapper>
        {sliceDetails?.overline && <Overline>{t(sliceDetails.overline)}</Overline>}
        {sliceDetails?.heading?.text && <Heading>{t(sliceDetails.heading.text)}</Heading>}
        {sliceDetails?.subheading?.text && <SubHeading>{t(sliceDetails.subheading.text)}</SubHeading>}
        <PathogenListBody>
          <PathogenGrid>
            {pathogensData.map((pathogen) => (
              <PathogenCard key={pathogen.code} pathogenData={pathogen} />
            ))}
          </PathogenGrid>
          <ImageWrap>
            <Image src={sliceDetails?.image?.url} alt={sliceDetails?.image?.alt} />
          </ImageWrap>
        </PathogenListBody>
      </Wrapper>
    </PathogenListContainer>
  );
};

export default PathogenList;

const PathogenListContainer = styled.div``;

const Wrapper = styled.div`
  width: 90vw;
  max-width: 1200px;
  margin: 0 auto;
  padding: 96px 0;
  text-align: center;
  color: ${Colors.navyBlue};
`;

const Overline = styled.div`
  ${TEXT_STYLES[TextVariant.UpperText4]};
  text-align: center;
  margin: 0 auto 16px;
`;

const Heading = styled.h2`
  ${TEXT_STYLES[TextVariant.Heading4]};
  text-align: center;
  margin: 0 auto 16px;

  @media (max-width: 450px) {
    ${TEXT_STYLES[TextVariant.Heading5]};
  }
`;

const SubHeading = styled.h2`
  ${TEXT_STYLES[TextVariant.BodyRegularLG]};
  text-align: center;
  max-width: 640px;
  margin: 0 auto;

  @media (max-width: 450px) {
    ${TEXT_STYLES[TextVariant.BodyRegularMD]};
  }
`;

const PathogenListBody = styled.div`
  display: grid;
  grid-template-columns: 3fr 1fr;
  gap: 24px;
  margin-top: 48px;

  @media (max-width: 1024px) {
    grid-template-columns: 2fr, 1fr;
    gap: 16px;
  }

  @media (max-width: 800px) {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`;

const PathogenGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }

  @media (max-width: 800px) {
    display: flex;
    flex-direction: column;
  }
`;

const ImageWrap = styled.div`
  position: relative;
`;

const Image = styled(LazyLoadImage)`
  object-fit: cover;
  border-radius: 12px;
  height: 100%;
  position: absolute;
  left: 0;

  @media (max-width: 800px) {
    display: none;
  }
`;
